import { Box, Chip, Stack } from '@mui/material';
import { ODSUploadToCloud } from '@telekom-ods/react-icons';
import { useField, useFormikContext, type FormikErrors } from 'formik';
import React, { type FC, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { UploadFile } from '../../InputFields';

interface FormikUploadFileProps {
  name: string;
  title?: string;
  description?: string;
  infoText?: string;
  icon?: ReactNode;
  disabled?: boolean | string;
  multiple?: boolean;
  onFileUpload?: (
    file: File,
    event: React.ChangeEvent<HTMLInputElement>,
    helpers: {
      setFieldValue: (
        field: string,
        value: unknown,
        shouldValidate?: boolean
      ) => Promise<FormikErrors<Record<string, unknown>>>;
      setFieldError: (field: string, message: string) => void;
    }
  ) => void;
  maxFileSize?: number;
}

export const FormikUploadFile: FC<FormikUploadFileProps> = ({
  name,
  title,
  description = '',
  infoText = '',
  icon = <ODSUploadToCloud />,
  disabled = false,
  multiple = false,
  onFileUpload,
  maxFileSize = 5 * 1024 * 1024, // 5MB default
}) => {
  const intl = useIntl();
  const resolvedTitle = title ?? intl.formatMessage({ id: 'uploadFile' });
  const [, meta] = useField(name);
  const { values, setFieldValue, setFieldError } = useFormikContext<Record<string, unknown>>();
  const isDisabled = typeof disabled === 'string' ? !values[disabled] : disabled;

  const currentFiles = values[name] as File | File[] | null;

  const getFilesArray = (): File[] => {
    if (!currentFiles) return [];
    return Array.isArray(currentFiles) ? currentFiles : [currentFiles];
  };

  const filesArray = getFilesArray();

  const handleSingleFileUpload = (file: File, event: React.ChangeEvent<HTMLInputElement>): void => {
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      setFieldError(name, intl.formatMessage({ id: 'fileSizeExceedsLimit' }, { size: maxSizeMB }));
      return;
    }

    setFieldValue(name, file);

    if (onFileUpload) {
      onFileUpload(file, event, {
        setFieldValue: setFieldValue as keyof typeof setFieldValue,
        setFieldError,
      });
    }
  };

  const handleMultipleFileUpload = (
    file: File,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      setFieldError(name, intl.formatMessage({ id: 'fileSizeExceedsLimit' }, { size: maxSizeMB }));
      return;
    }

    const existingFiles = Array.isArray(currentFiles) ? currentFiles : [];
    setFieldValue(name, [...existingFiles, file]);

    if (onFileUpload) {
      onFileUpload(file, event, {
        setFieldValue: setFieldValue as keyof typeof setFieldValue,
        setFieldError,
      });
    }
  };

  const handleFileUpload = (
    file: FileList | File,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const actualFile = file instanceof FileList ? file[0] : file;

    if (!actualFile) return;

    const uploadMethod = multiple ? handleMultipleFileUpload : handleSingleFileUpload;
    uploadMethod(actualFile, event);
  };

  const handleRemoveFile = (fileToRemove: File): void => {
    const updatedFiles = multiple ? filesArray.filter(f => f !== fileToRemove) : [];
    setFieldValue(name, updatedFiles.length > 0 ? updatedFiles : null);
  };

  const displayInfoText = (): string => {
    if (meta.touched && meta.error) {
      return meta.error;
    }
    if (filesArray.length > 0) {
      const totalSize = filesArray.reduce((sum, file) => sum + file.size, 0);
      return intl.formatMessage(
        { id: 'filesUploadedInfo' },
        { count: filesArray.length, size: (totalSize / 1024).toFixed(1) }
      );
    }
    return infoText;
  };

  return (
    <Box>
      <UploadFile
        title={resolvedTitle}
        description={description}
        icon={icon}
        infoText={displayInfoText()}
        onFileUpload={(files: File | FileList) => {
          handleFileUpload(files, { target: { files } } as React.ChangeEvent<HTMLInputElement>);
        }}
        disabled={isDisabled}
      />

      {filesArray.length > 0 && (
        <Stack
          direction='row'
          spacing={1}
          sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}
        >
          {filesArray.map(file => (
            <Chip
              key={`${file.name}-${file.lastModified.toString()}-${file.size.toString()}`}
              label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
              onDelete={() => {
                handleRemoveFile(file);
              }}
              color='primary'
              variant='outlined'
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
